package one.devsbox.www.ui.campaigns.edit

import androidx.compose.animation.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.*
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.toLocalDateTime
import one.devsbox.commons.EMPTY
import one.devsbox.commons.nowDate
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.ui.resources.MediaCard
import one.devsbox.www.ui.widgets.*
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import kotlin.time.Clock
import kotlin.time.Instant

@Composable
@ComposableNavNode(ArgsId::class)
fun CampaignEUI() {
    with(CampaignECtx.get()) {
        ContentCampaignEUI()
    }
}

@Composable
context(ctx: CampaignECtx)
private fun ContentCampaignEUI() {
    Box(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()

                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> {
                    SuccessUI(state.nnvl)
                }
            }
        }

        // Warning Dialog
        ctx.vm.warningMessage?.let { msg ->
            AlertDialog(
                onDismissRequest = { ctx.action.clearWarning() },
                title = { Text(stringResource(Res.string.ui_devices_edit_deviceeui_error)) },
                text = { Text(stringResource(msg)) },
                confirmButton = {
                    TextButton(onClick = { ctx.action.clearWarning() }) {
                        Text(stringResource(Res.string.ui_devices_edit_deviceeui_ok))
                    }
                }
            )
        }
    }
}

@Composable
context(ctx: CampaignECtx)
private fun SuccessUI(editor: CampaignECtx.Editor) {
    Column(modifier = Modifier.fillMaxSize()) {
        // Header
        EditorHeader()

        // Main Content Area with Animation
        Box(modifier = Modifier.weight(1f)) {
            AnimatedContent(
                targetState = ctx.vm.currentStep,
                transitionSpec = {
                    if (targetState > initialState) {
                        slideInHorizontally { it } + fadeIn() togetherWith
                                slideOutHorizontally { -it } + fadeOut()
                    } else {
                        slideInHorizontally { -it } + fadeIn() togetherWith
                                slideOutHorizontally { it } + fadeOut()
                    }
                }
            ) { step ->
                when (step) {
                    0 -> Step1Identity(editor)
                    1 -> Step2Locations(editor)
                    2 -> Step3Assets(editor)
                }
            }
        }

        // Bottom Bar
        EditorBottomBar(editor)
    }
}

@Composable
context(ctx: CampaignECtx)
private fun EditorHeader() {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(16.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(
                imageVector = Svg.Bsoft,
                contentDescription = null,
                modifier = Modifier.size(24.dp)
                    .align(Alignment.CenterVertically),
                tint = MaterialTheme.colorScheme.primary
            )
            Spacer(Modifier.width(12.dp))
            Text(
                text = stringResource(Res.string.ui_campaigns_edit_title),
                style = MaterialTheme.typography.titleMedium,
                modifier = Modifier.align(Alignment.CenterVertically)
                    .padding(top = 4.dp),
                letterSpacing = 1.sp,
            )
            Spacer(Modifier.width(8.dp))
            Text(
                stringResource(Res.string.ui_campaigns_edit_step_format, ctx.vm.currentStep + 1),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.align(Alignment.CenterVertically)
                    .padding(top = 4.dp),
                letterSpacing = 1.sp,
            )
        }

        IconButton(onClick = { ctx.nav.up() }) {
            Icon(
                Svg.CloseOutline,
                contentDescription = stringResource(Res.string.ui_campaigns_edit_btn_close),
                tint = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
context(ctx: CampaignECtx)
private fun Step1Identity(editor: CampaignECtx.Editor) {
    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val isWide = maxWidth >= 1000.dp
        if (isWide) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(16.dp),
                horizontalArrangement = Arrangement.spacedBy(32.dp)
            ) {
                IdentityContent(
                    editor = editor,
                    isWide = true,
                    modifier = Modifier
                        .weight(1f)
                        .verticalScroll(rememberScrollState())
                )

                // Info Sidebar
                Step1InfoSidebar()
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(16.dp)
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(32.dp)
            ) {
                IdentityContent(editor = editor, isWide = false)
                Step1InfoSidebar(modifier = Modifier.fillMaxWidth())
            }
        }
    }
}

@Composable
context(ctx: CampaignECtx)
private fun IdentityContent(
    editor: CampaignECtx.Editor,
    isWide: Boolean,
    modifier: Modifier = Modifier
) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(32.dp)) {
        Text(
            text = stringResource(Res.string.ui_campaigns_edit_identity_schedule),
            style = MaterialTheme.typography.headlineLarge,
            fontWeight = FontWeight.Bold
        )

        // Primary Designation
        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
            LabelSection(stringResource(Res.string.ui_campaigns_edit_primary_designation))

            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = Color.Transparent),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
            ) {
                Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    LabelField(stringResource(Res.string.ui_campaigns_edit_campaign_name))
                    OutlinedTextField(
                        value = editor.name.text,
                        onValueChange = { editor.name.set(it) },
                        modifier = Modifier.fillMaxWidth(),
                        placeholder = {
                            Text(
                                stringResource(Res.string.ui_campaigns_edit_campaign_name_placeholder),
                                color = MaterialTheme.colorScheme.outline
                            )
                        },
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = MaterialTheme.colorScheme.primary,
                            unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
                        ),
                        shape = RoundedCornerShape(4.dp)
                    )

                }
            }
        }

        // Temporal Parameters
        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
            LabelSection(stringResource(Res.string.ui_campaigns_edit_temporal_parameters))

            if (isWide) {
                Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                    TemporalCard(
                        label = stringResource(Res.string.ui_campaigns_edit_commencement),
                        subLabel = stringResource(Res.string.ui_campaigns_edit_activation_date),
                        toggleLabel = stringResource(Res.string.ui_campaigns_edit_immediate),
                        date = editor.startDate.state,
                        onToggle = { ctx.action.toggleStartDate() },
                        onDateChange = { editor.startDate.set(it) }
                    )
                    TemporalCard(
                        label = stringResource(Res.string.ui_campaigns_edit_termination),
                        subLabel = stringResource(Res.string.ui_campaigns_edit_expiration_date),
                        toggleLabel = stringResource(Res.string.ui_campaigns_edit_perpetual),
                        date = editor.endDate.state,
                        onToggle = { ctx.action.toggleEndDate() },
                        onDateChange = { editor.endDate.set(it) }
                    )
                }
            } else {
                Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                    TemporalCard(
                        stringResource(Res.string.ui_campaigns_edit_commencement),
                        stringResource(Res.string.ui_campaigns_edit_activation_date),
                        stringResource(Res.string.ui_campaigns_edit_immediate),
                        editor.startDate.state,
                        modifier = Modifier.fillMaxWidth(),
                        onToggle = { ctx.action.toggleStartDate() },
                        onDateChange = { editor.startDate.set(it) }
                    )
                    TemporalCard(
                        stringResource(Res.string.ui_campaigns_edit_termination),
                        stringResource(Res.string.ui_campaigns_edit_expiration_date),
                        stringResource(Res.string.ui_campaigns_edit_perpetual),
                        editor.endDate.state,
                        modifier = Modifier.fillMaxWidth(),
                        onToggle = { ctx.action.toggleEndDate() },
                        onDateChange = { editor.endDate.set(it) }
                    )
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun TemporalCard(
    label: String,
    subLabel: String,
    toggleLabel: String,
    date: LocalDate,
    modifier: Modifier = Modifier.width(384.dp),
    onToggle: () -> Unit,
    onDateChange: (LocalDate) -> Unit
) {
    var showDatePicker by remember { mutableStateOf(false) }

    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(Modifier) {
            Row(
                Modifier.fillMaxWidth()
                    .clickable { onToggle() }
                    .padding(16.dp),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Column(Modifier.align(Alignment.CenterVertically)) {
                    Text(
                        label,
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                    Text(
                        subLabel,
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,

                        )
                }
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        toggleLabel,
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                    Spacer(Modifier.width(8.dp))
                    Switch(
                        checked = date == LocalDate.EMPTY,
                        onCheckedChange = { onToggle() },
                        colors = SwitchDefaults.colors(
                            checkedThumbColor = MaterialTheme.colorScheme.onPrimary,
                            checkedTrackColor = MaterialTheme.colorScheme.primary
                        ),
                        modifier = Modifier.scale(0.7f)
                    )
                }
            }

            AnimatedVisibility(visible = date != LocalDate.EMPTY) {
                Column {
                    Spacer(Modifier.height(16.dp))
                    OutlinedButton(
                        onClick = { showDatePicker = true },
                        modifier = Modifier.padding(16.dp).fillMaxWidth(),
                        shape = RoundedCornerShape(4.dp),
                        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                Svg.CalendarRangeOutline,
                                null,
                                Modifier.size(16.dp),
                                tint = MaterialTheme.colorScheme.onSurface
                            )
                            Spacer(Modifier.width(12.dp))
                            Text(
                                date.toString(),
                                color = MaterialTheme.colorScheme.onSurface
                            )
                        }
                    }
                }
            }
        }
    }

    if (showDatePicker) {
        val datePickerState = rememberDatePickerState(
            initialSelectedDateMillis = date.let {
                if (it == LocalDate.EMPTY) null else it.atStartOfDayIn(TimeZone.UTC)
                    .toEpochMilliseconds()
            },
            selectableDates = object : SelectableDates {
                override fun isSelectableDate(utcTimeMillis: Long): Boolean {
                    val today = Clock.nowDate().atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds()
                    return utcTimeMillis >= today
                }
            }
        )

        DatePickerDialog(
            onDismissRequest = { showDatePicker = false },
            confirmButton = {
                TextButton(onClick = {
                    datePickerState.selectedDateMillis?.let {
                        val selectedDate =
                            Instant.fromEpochMilliseconds(it).toLocalDateTime(TimeZone.UTC).date
                        onDateChange(selectedDate)
                    }
                    showDatePicker = false
                }) {
                    Text(stringResource(Res.string.dialog_ok))
                }
            },
            dismissButton = {
                TextButton(onClick = { showDatePicker = false }) {
                    Text(stringResource(Res.string.dialog_cancel))
                }
            }
        ) {
            DatePicker(state = datePickerState, showModeToggle = false)
        }
    }
}

@Composable
private fun Step1InfoSidebar(modifier: Modifier = Modifier.width(280.dp)) {
    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            InfoSection(
                title = stringResource(Res.string.ui_campaigns_edit_nomenclature_title),
                content = stringResource(Res.string.ui_campaigns_edit_nomenclature_desc)
            )
            InfoSection(
                title = stringResource(Res.string.ui_campaigns_edit_scheduling_title),
                content = stringResource(Res.string.ui_campaigns_edit_scheduling_desc)
            )
            InfoSection(
                title = stringResource(Res.string.ui_campaigns_edit_duration_title),
                content = stringResource(Res.string.ui_campaigns_edit_duration_desc)
            )
        }
    }
}

@Composable
private fun InfoSection(title: String, content: String) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        LabelSection(title)
        Text(
            content,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            lineHeight = 16.sp
        )
    }
}

@Composable
context(ctx: CampaignECtx)
private fun Step2Locations(editor: CampaignECtx.Editor) {
    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val isWide = maxWidth >= 1000.dp
        if (isWide) {
            Column(
                Modifier.padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Step2Title()
                Row(
                    modifier = Modifier
                        .fillMaxSize(),
                    horizontalArrangement = Arrangement.spacedBy(32.dp)
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Step2Search(editor = editor)
                        LocationsContent(editor = editor)
                    }
                    Step2Summary(editor = editor, modifier = Modifier.width(280.dp))
                }
            }
        } else {
            val filtered = remember(editor.locations, editor.searchQuery) {
                editor.locations.filter { loc ->
                    loc.name.contains(editor.searchQuery, ignoreCase = true) ||
                            loc.devices.any {
                                it.name.contains(
                                    editor.searchQuery,
                                    ignoreCase = true
                                )
                            }
                }
            }
            LazyColumn(
                modifier = Modifier
                    .fillMaxSize(),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                stickyHeader {
                    Step2Title()
                }
                item {
                    Step2Summary(editor = editor, modifier = Modifier.fillMaxWidth())

                }
                stickyHeader {
                    Step2Search(editor = editor)
                }
                if (filtered.isEmpty()) {
                    item {
                        Box(
                            modifier = Modifier.fillMaxWidth(),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.Center
                            ) {
                                Icon(
                                    imageVector = Svg.LocationOutline,
                                    contentDescription = null,
                                    tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                                    modifier = Modifier.size(48.dp)
                                )
                                Spacer(Modifier.height(16.dp))
                                Text(
                                    text = stringResource(Res.string.ui_campaigns_edit_no_locations_found),
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                                )
                            }
                        }

                    }
                } else {
                    stickyHeader {
                        Row(
                            modifier = Modifier.fillMaxWidth()
                                .padding(horizontal = 16.dp, vertical = 8.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            LabelSection(stringResource(Res.string.ui_campaigns_edit_deployment_zone))
                            Text(
                                text = stringResource(Res.string.ui_campaigns_edit_nodes),
                                style = MaterialTheme.typography.labelMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                fontWeight = FontWeight.Bold,
                                letterSpacing = 1.sp
                            )
                        }
                    }
                }
                items(filtered) { loc ->
                    LocationDeviceGroup(editor, loc)
                }
            }
        }
    }
}

@Composable
context(ctx: CampaignECtx)
private fun Step2Title() {
    Text(
        text = stringResource(Res.string.ui_campaigns_edit_locations_title),
        style = MaterialTheme.typography.headlineMedium,
        fontWeight = FontWeight.Bold
    )
}

@Composable
context(ctx: CampaignECtx)
private fun Step2Search(editor: CampaignECtx.Editor) {
    val searchState =
        remember { androidx.compose.foundation.text.input.TextFieldState(editor.searchQuery) }
    LaunchedEffect(searchState.text) {
        editor.searchQuery = searchState.text.toString()
    }

    SearchBox(
        state = searchState,
        hint = stringResource(Res.string.ui_campaigns_edit_search_placeholder),
        modifier = Modifier.fillMaxWidth(),
        actions = listOf(
            {
                val anySelected = editor.selectedDeviceIds.isNotEmpty()
                TextButton(
                    onClick = {
                        if (anySelected) {
                            editor.selectedDeviceIds.clear()
                        } else {
                            editor.locations.forEach { loc ->
                                loc.devices.forEach { dev ->
                                    if (!editor.selectedDeviceIds.contains(dev.id)) editor.toggleDevice(
                                        dev.id
                                    )
                                }
                            }
                        }
                    }
                ) {
                    Text(
                        text = if (anySelected) stringResource(Res.string.ui_campaigns_edit_btn_unselect_all) else stringResource(
                            Res.string.ui_campaigns_edit_btn_select_all
                        ),
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.Bold
                    )
                }
            },
            {
                TextButton(
                    onClick = {
                        editor.selectedDeviceIds.clear()
                    }
                ) {
                    Text(
                        text = stringResource(Res.string.ui_campaigns_edit_btn_clear),
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier.padding(end = 8.dp)
                    )
                }
            }
        )
    )
    Spacer(Modifier.height(16.dp))
}

@Composable
context(ctx: CampaignECtx)
private fun LocationsContent(editor: CampaignECtx.Editor, modifier: Modifier = Modifier) {
    Column(modifier = modifier) {
        val filtered = remember(editor.locations, editor.searchQuery) {
            editor.locations.filter { loc ->
                loc.name.contains(editor.searchQuery, ignoreCase = true) ||
                        loc.devices.any { it.name.contains(editor.searchQuery, ignoreCase = true) }
            }
        }

        if (filtered.isEmpty()) {
            Box(
                modifier = Modifier.fillMaxWidth().weight(1f),
                contentAlignment = Alignment.Center
            ) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Icon(
                        imageVector = Svg.LocationOutline,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                        modifier = Modifier.size(48.dp)
                    )
                    Spacer(Modifier.height(16.dp))
                    Text(
                        text = stringResource(Res.string.ui_campaigns_edit_no_locations_found),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                    )
                }
            }
        } else {
            // Locations List Header
            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                LabelSection(stringResource(Res.string.ui_campaigns_edit_deployment_zone))
                Text(
                    text = stringResource(Res.string.ui_campaigns_edit_nodes),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = 1.sp
                )
            }

            Spacer(Modifier.height(16.dp))

            // Locations List
            LazyColumn(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                items(filtered) { loc ->
                    LocationDeviceGroup(editor, loc)
                }
            }
        }
    }
}

@Composable
context(ctx: CampaignECtx)
private fun LocationDeviceGroup(editor: CampaignECtx.Editor, loc: SharedIO.LocationWithDevices) {
    val searchActive = editor.searchQuery.isNotEmpty()
    val matchInDevices = remember(loc, editor.searchQuery) {
        loc.devices.any { it.name.contains(editor.searchQuery, ignoreCase = true) }
    }
    var expanded by remember { mutableStateOf(false) }

    val isExpanded = expanded || (searchActive && matchInDevices)

    val allSelected = loc.devices.all { editor.selectedDeviceIds.contains(it.id) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(1.dp, MaterialTheme.colorScheme.outlineVariant, RoundedCornerShape(4.dp))
            .background(Color.Transparent)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Left side: Selection logic
            Row(
                modifier = Modifier
                    .weight(1f)
                    .clickable {
                        val select = !allSelected
                        loc.devices.forEach { dev ->
                            val isSelected = editor.selectedDeviceIds.contains(dev.id)
                            if (select != isSelected) editor.toggleDevice(dev.id)
                        }
                    }
                    .padding(16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Checkbox(
                    checked = allSelected,
                    onCheckedChange = null // Click handled by parent row
                )
                Spacer(Modifier.width(12.dp))
                Text(
                    loc.name,
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Bold
                )
            }

            // Vertical Divider
            Box(
                modifier = Modifier
                    .width(1.dp)
                    .height(32.dp)
                    .background(MaterialTheme.colorScheme.outlineVariant)
            )

            // Right side: Expansion logic
            Row(
                modifier = Modifier
                    .clickable { expanded = !expanded }
                    .padding(16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    "${loc.devices.count { editor.selectedDeviceIds.contains(it.id) }}/${loc.devices.size}",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                Spacer(Modifier.width(12.dp))
                Icon(
                    imageVector = Svg.ChevronRight,
                    contentDescription = null,
                    modifier = Modifier
                        .size(16.dp)
                        .rotate(if (isExpanded) -90f else 90f),
                    tint = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }

        if (isExpanded) {
            Column(Modifier.padding(start = 32.dp, end = 16.dp, bottom = 16.dp)) {
                loc.devices.forEach { dev ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable { editor.toggleDevice(dev.id) }
                            .padding(vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Checkbox(
                            checked = editor.selectedDeviceIds.contains(dev.id),
                            onCheckedChange = { editor.toggleDevice(dev.id) }
                        )
                        Spacer(Modifier.width(12.dp))
                        Text(dev.name, style = MaterialTheme.typography.bodySmall)
                    }
                }
            }
        }
    }
}

@Composable
context(ctx: CampaignECtx)
private fun Step2Summary(editor: CampaignECtx.Editor, modifier: Modifier = Modifier.width(280.dp)) {
    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            Text(
                text = stringResource(Res.string.ui_campaigns_edit_targeting_summary),
                style = MaterialTheme.typography.labelMedium,
                fontWeight = FontWeight.Bold,
                letterSpacing = 2.sp
            )
            SummaryItem(
                label = stringResource(Res.string.ui_campaigns_edit_locations_selected),
                value = editor.locations.count {
                    it.devices.any { dev ->
                        editor.selectedDeviceIds.contains(
                            dev.id
                        )
                    }
                },
                total = editor.locations.count()
            )

            SummaryItem(
                label = stringResource(Res.string.ui_campaigns_edit_nodes_active),
                value = editor.selectedDeviceIds.size,
                total = editor.locations.sumOf { it.devices.count() },
                valueColor = MaterialTheme.colorScheme.primary
            )

            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                LabelSection(stringResource(Res.string.ui_campaigns_edit_targeting_requirements_title))
                Text(
                    text = stringResource(Res.string.ui_campaigns_edit_targeting_requirements_desc),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    lineHeight = 18.sp
                )
            }
        }
    }
}

@Composable
private fun SummaryItem(label: String, value: Int, total: Int, valueColor: Color? = null) {
    val resolvedColor = valueColor ?: MaterialTheme.colorScheme.onSurface
    Column {
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Row(verticalAlignment = Alignment.Bottom) {
            Text(
                text = value.toString(),
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                color = resolvedColor
            )
            Spacer(Modifier.size(4.dp))
            Text(
                text = " / $total",
                style = MaterialTheme.typography.titleMedium,
                color = MaterialTheme.colorScheme.outline,
                modifier = Modifier.align(Alignment.Bottom)
            )
        }
    }
}

@Composable
context(ctx: CampaignECtx)
private fun Step3Assets(editor: CampaignECtx.Editor) {
    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val isWide = maxWidth >= 1000.dp
        if (isWide) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(16.dp),
                horizontalArrangement = Arrangement.spacedBy(32.dp)
            ) {
                AssetsContent(editor = editor, modifier = Modifier.weight(1f), isWide = true)
                Step3InfoSidebar(editor = editor, modifier = Modifier.width(280.dp))
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(16.dp)
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                AssetsContent(editor = editor, isWide = false)
                Step3InfoSidebar(editor = editor, modifier = Modifier.fillMaxWidth())
            }
        }
    }
}

@Composable
context(ctx: CampaignECtx)
private fun AssetsContent(
    editor: CampaignECtx.Editor,
    isWide: Boolean,
    modifier: Modifier = Modifier
) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(16.dp)) {
        val aspectRatios = editor.getSelectedAspectRatios()

        Text(
            text = stringResource(Res.string.ui_campaigns_edit_assets_title),
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold
        )

        // Visual picker cards
        if (isWide) {
            Row(
                modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                aspectRatios.forEach { ar ->
                    AssetCard(editor, ar) {
                        ctx.action.pickMedia(ar)
                    }
                }
            }
        } else {
            aspectRatios.forEach { ar ->
                AssetCard(editor, ar, modifier = Modifier.fillMaxWidth()) {
                    ctx.action.pickMedia(ar)
                }
            }
        }

        Spacer(Modifier.height(16.dp))

        // Duration of visuals input field
        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = CardDefaults.cardColors(containerColor = Color.Transparent),
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
        ) {
            Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                LabelSection(stringResource(Res.string.ui_campaigns_edit_duration_field_title))
                OutlinedTextField(
                    value = if (editor.durationSeconds == 0) "" else editor.durationSeconds.toString(),
                    onValueChange = { newValue ->
                        val clean = newValue.filter { it.isDigit() }
                        editor.durationSeconds = clean.toIntOrNull() ?: 0
                    },
                    modifier = Modifier.fillMaxWidth(if (isWide) 0.3f else 1f),
                    placeholder = {
                        Text(
                            "10",
                            color = MaterialTheme.colorScheme.outline
                        )
                    },
                    suffix = {
                        Text(
                            stringResource(Res.string.ui_campaigns_edit_duration_seconds_suffix),
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    },
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = MaterialTheme.colorScheme.primary,
                        unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
                    ),
                    shape = RoundedCornerShape(4.dp)
                )
                Spacer(Modifier.height(4.dp))
                Text(
                    text = stringResource(Res.string.ui_campaigns_edit_duration_field_desc),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

@Composable
context(ctx: CampaignECtx)
private fun Step3InfoSidebar(
    editor: CampaignECtx.Editor,
    modifier: Modifier = Modifier.width(280.dp)
) {
    val aspectRatios = editor.getSelectedAspectRatios()
    val numVisuals = aspectRatios.size

    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            LabelSection(stringResource(Res.string.ui_campaigns_edit_visual_guidelines_title))
            val explanation = if (numVisuals <= 1) {
                stringResource(Res.string.ui_campaigns_edit_visual_guidelines_single)
            } else {
                stringResource(Res.string.ui_campaigns_edit_visual_guidelines_multiple, numVisuals)
            }
            Text(
                text = explanation,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                lineHeight = 18.sp
            )
        }
    }
}

@Composable
context(ctx: CampaignECtx)
private fun AssetCard(
    editor: CampaignECtx.Editor,
    ar: String,
    modifier: Modifier = Modifier.width(360.dp),
    onClick: () -> Unit
) {
    val media = editor.selectedMediaByAspectRatio[ar]
    val icon = remember(ar) {
        val (w, h) = ar.split(":").map { it.toInt() }
        if (w < h) Svg.DisplayVerticalOutline
        else Svg.DisplayHorizontalOutline
    }
    Card(
        onClick = onClick,
        modifier = modifier,
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(Modifier.padding(20.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    modifier = Modifier.size(24.dp),
                    tint = MaterialTheme.colorScheme.primary
                )
                Spacer(Modifier.width(12.dp))
                Text(
                    text = ar,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.primary
                )
            }
            Spacer(Modifier.height(16.dp))

            if (media != null) {
                MediaCard(media)
            } else {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(
                            1.dp,
                            MaterialTheme.colorScheme.outlineVariant,
                            RoundedCornerShape(4.dp)
                        )
                        .background(Color.Transparent),
                    contentAlignment = Alignment.Center
                ) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        modifier = Modifier.padding(vertical = 32.dp).fillMaxWidth()
                    ) {
                        Icon(
                            imageVector = Svg.ImageOutline,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                            modifier = Modifier.size(48.dp)
                        )
                        Spacer(Modifier.height(16.dp))
                        Text(
                            text = stringResource(Res.string.ui_campaigns_edit_select_from_library),
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onSurface,
                            style = MaterialTheme.typography.bodyMedium
                        )
                        Spacer(Modifier.height(8.dp))
                        Text(
                            text = stringResource(Res.string.ui_campaigns_edit_select_from_library_desc),
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }
            }
        }
    }
}

@Composable
context(ctx: CampaignECtx)
private fun EditorBottomBar(editor: CampaignECtx.Editor) {
    Column(
        modifier = Modifier.fillMaxWidth().background(Color.Transparent)
    ) {
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            TextButton(
                onClick = { ctx.action.prevStep() },
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(
                        imageVector = Svg.ChevronRight,
                        contentDescription = null,
                        modifier = Modifier.size(16.dp).rotate(180f),
                        tint = MaterialTheme.colorScheme.onSurface
                    )
                    Spacer(Modifier.width(8.dp))
                    Text(
                        text = stringResource(Res.string.ui_campaigns_edit_btn_back),
                        color = MaterialTheme.colorScheme.onSurface
                    )
                }
            }

            Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                if (ctx.vm.currentStep == 2) {
                    val isNewOrDraft =
                        ctx.args.id <= 0 || editor.campaign.state == one.devsbox.shared.ads.CampaignState.DRAFT.mask
                    if (isNewOrDraft) {
                        OutlinedButton(
                            onClick = { ctx.action.saveAsDraft() },
                            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(
                                    Svg.ContentSaveOutline,
                                    null,
                                    Modifier.size(18.dp),
                                    tint = MaterialTheme.colorScheme.primary
                                )
                                Spacer(Modifier.width(8.dp))
                                Text(
                                    text = stringResource(Res.string.ui_campaigns_edit_btn_save_draft),
                                    color = MaterialTheme.colorScheme.primary
                                )
                            }
                        }
                    }
                    Button(
                        onClick = { ctx.action.activateCampaign() },
                        colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.primary)
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                Svg.Play,
                                null,
                                Modifier.size(18.dp),
                                tint = MaterialTheme.colorScheme.onPrimary
                            )
                            Spacer(Modifier.width(8.dp))
                            Text(
                                text = stringResource(Res.string.ui_campaigns_edit_btn_activate),
                                color = MaterialTheme.colorScheme.onPrimary
                            )
                        }
                    }
                } else {
                    Button(
                        onClick = { ctx.action.nextStep() },
                        colors = ButtonDefaults.buttonColors(containerColor = Color.Transparent),
                        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Text(
                                text = if (ctx.vm.currentStep == 0) stringResource(Res.string.ui_campaigns_edit_btn_pick_locations) else stringResource(
                                    Res.string.ui_campaigns_edit_btn_pick_assets
                                ),
                                color = MaterialTheme.colorScheme.primary
                            )
                            Spacer(Modifier.width(8.dp))
                            Icon(
                                Svg.ChevronRight,
                                null,
                                Modifier.size(16.dp),
                                tint = MaterialTheme.colorScheme.primary
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FieldGroup(label: String, content: @Composable () -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(0.dp)) {
        LabelField(text = label)
        content()
    }
}